import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { fetchCategories, fetchGames } from '@/api/library';
import { GameTile } from '@/components/library/GameTile';
import { LiveFamilyCard } from '@/components/library/LiveFamilyCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { SearchInput } from '@/components/ui/SearchInput';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n } from '@/i18n';
import { familyGameId, LIVE_FAMILIES } from '@/lib/liveFamilies';
import type { HubCategory, HubGame } from '@/types';

const FALLBACK_CATEGORIES: HubCategory[] = [
  { id: 'live', label: 'Live' },
  { id: 'slot', label: 'Slot' },
];

type StatusFilter = 'all' | 'available' | 'coming_soon' | 'maintenance';

interface LibraryPageProps {
  onCategoriesLoaded?: (categories: HubCategory[]) => void;
  onCategoryChange?: (categoryId: string) => void;
}

export function LibraryPage({ onCategoriesLoaded, onCategoryChange }: LibraryPageProps) {
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories, setCategories] = useState<HubCategory[]>(FALLBACK_CATEGORIES);
  const [games, setGames] = useState<HubGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiFailed, setApiFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const category = searchParams.get('category') ?? categories[0]?.id ?? 'live';
  /* Live is browsed by product card, so it never shows the flat game grid. */
  const liveLanding = category === 'live';
  const slotLanding = category === 'slot' || category === 'slots';
  const search = searchParams.get('q') ?? '';
  const status = (searchParams.get('status') as StatusFilter) ?? 'all';

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    setApiFailed(false);

    try {
      const [cats, gameList] = await Promise.all([
        fetchCategories(accessToken, sessionId).catch(() => FALLBACK_CATEGORIES),
        fetchGames(accessToken, { category, namePrefix: search || undefined, limit: 100 }, sessionId),
      ]);

      if (cats.length > 0) {
        setCategories(cats);
        onCategoriesLoaded?.(cats);
      }

      setGames(gameList);
    } catch {
      setApiFailed(true);
      setGames([]);
      setError(t('library.unavailableMessage'));
    } finally {
      setLoading(false);
    }
  }, [accessToken, sessionId, category, search, locale, onCategoriesLoaded, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    onCategoryChange?.(category);
  }, [category, onCategoryChange]);

  const filteredGames = useMemo(() => {
    return games.filter((game) => {
      if (status === 'all') return true;
      if (status === 'maintenance') return game.flags?.isInMaintenance;
      if (status === 'coming_soon') return game.flags?.isComingSoon;
      if (status === 'available') {
        return !game.flags?.isComingSoon && !game.flags?.isInMaintenance;
      }
      return true;
    });
  }, [games, status]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  return (
    <section className="library-page site-pad-x">
      <header className="page-header reveal is-in">
        <p className="eyebrow">{liveLanding ? t('game.liveCasino') : t('library.eyebrow')}</p>
        <h1 className="page-title display">
          {liveLanding
            ? t('library.liveTitle')
            : slotLanding
              ? t('library.slotTitle')
              : t('library.title')}
        </h1>
        <p className="page-lead">
          {liveLanding ? t('library.liveLead') : t('library.lead')}
        </p>
      </header>

      {liveLanding ? (
        <div className="live-family-grid reveal is-in reveal-delay-1">
          {LIVE_FAMILIES.map((family) => (
            <LiveFamilyCard
              key={family.id}
              family={family}
              gameId={family.active ? familyGameId(games, family.id) : null}
            />
          ))}
        </div>
      ) : null}

      {liveLanding ? null : (
        <>
          <div className="library-toolbar reveal is-in reveal-delay-1">
            <SearchInput
              id="library-search"
              value={search}
              onChange={(value) => updateParam('q', value)}
              placeholder={t('library.search')}
            />
            <select
              className="game-filter__control"
              value={status}
              onChange={(e) => updateParam('status', e.target.value)}
              aria-label={t('library.statusFilterAria')}
            >
              <option value="all">{t('library.statusAll')}</option>
              <option value="available">{t('status.available')}</option>
              <option value="coming_soon">{t('status.coming_soon')}</option>
              <option value="maintenance">{t('status.maintenance')}</option>
            </select>
          </div>

          {loading ? (
            <div className="page-loading">
              <Spinner label={t('library.loading')} />
            </div>
          ) : null}

          {!loading && error ? (
            <ErrorMessage message={error} title={t('library.unavailableTitle')} />
          ) : null}

          {!loading && !error && filteredGames.length === 0 ? (
            <EmptyState
              title={apiFailed ? t('library.unavailableTitle') : t('library.emptyTitle')}
              message={apiFailed ? t('library.apiFailedMessage') : t('library.emptyMessage')}
            />
          ) : null}

          {!loading && filteredGames.length > 0 ? (
            <div className="game-catalog__grid">
              {filteredGames.map((game) => (
                <GameTile
                  key={game.id}
                  game={game}
                  accessToken={accessToken}
                  sessionId={sessionId}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
